import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Tabs,
  Select,
  DatePicker,
  Button,
  Space,
  Table,
  Statistic,
  Tag,
  Modal,
  Popconfirm,
  App,
  Input,
} from 'antd'
import {
  TableOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { dataSourcesApi } from '../../services/api'
import Permission from '../../components/Permission'

const { RangePicker } = DatePicker
const { Option } = Select

// 模拟资产数据列表
const initialDataSource = [
  {
    key: '1',
    id: 'DAT001',
    name: '核心客户应收账款明细',
    type: '应收',
    risk: '低',
    source: '财务系统',
    summary: '包含核心客户近三年的应收账款流水及余额信息',
    value: 3200000,
    status: 'available',
    updateTime: '2024-01-15 10:30',
  },
  {
    key: '2',
    id: 'DAT002',
    name: '银行承兑汇票清单',
    type: '票据',
    risk: '低',
    source: '票据管理系统',
    summary: '在手银行承兑汇票的到期日与金额明细',
    value: 1800000,
    status: 'available',
    updateTime: '2024-01-15 09:20',
  },
  {
    key: '3',
    id: 'DAT003',
    name: '保理业务合同台账',
    type: '保理资产',
    risk: '中',
    source: '保理系统',
    summary: '已签约保理业务合同及对应应收账款信息',
    value: 2600000,
    status: 'used',
    updateTime: '2024-01-14 17:45',
  },
  {
    key: '4',
    id: 'DAT004',
    name: 'ABS底层资产池明细',
    type: 'ABS底层资产',
    risk: '中',
    source: 'ABS资产系统',
    summary: '已发行ABS对应的底层资产分层及现金流信息',
    value: 5400000,
    status: 'available',
    updateTime: '2024-01-14 16:10',
  },
  {
    key: '5',
    id: 'DAT005',
    name: '历史已核销资产清单',
    type: '应收',
    risk: '高',
    source: '历史档案',
    summary: '历史已核销资产明细，用于风险分析与建模',
    value: 800000,
    status: 'written_off',
    updateTime: '2024-01-10 11:05',
  },
]

const statusTextMap: Record<string, string> = {
  available: '可用',
  void: '作废',
  written_off: '已核销',
  used: '已使用',
}

const statusColorMap: Record<string, string> = {
  available: 'green',
  void: 'red',
  written_off: 'orange',
  used: 'blue',
}

type DataRow = typeof initialDataSource[0]
type FilterOverrides = {
  type?: string
  status?: string
  keyword?: string
  dateRange?: [Dayjs | null, Dayjs | null] | null
}

function normalizeDataList(res: any): DataRow[] {
  const raw = res?.data
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.list)
      ? raw.list
      : Array.isArray(raw)
        ? raw
        : Array.isArray(res?.list)
          ? res.list
          : Array.isArray(res)
            ? res
            : []
  return list.map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? item.data_id ?? '',
    name: item.name ?? item.data_name ?? '',
    type: item.type ?? '应收',
    risk: item.risk ?? '中',
    source: item.source ?? '',
    summary: item.summary ?? item.description ?? '',
    value: Number(item.value ?? item.amount ?? 0),
    status: item.status ?? 'available',
    updateTime: item.updateTime ?? item.updated_at ?? item.update_time ?? '',
  }))
}

const DataVisualization = () => {
  const { message } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('table')
  // 当前筛选控件的值（仅用于表单展示），真正生效的条件在 applied* 中
  const [assetType, setAssetType] = useState('all')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  // 已点击“筛选”后生效的条件
  const [appliedAssetType, setAppliedAssetType] = useState('all')
  const [appliedDateRange, setAppliedDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [tableData, setTableData] = useState<DataRow[]>(initialDataSource)
  const [listTotal, setListTotal] = useState(0)
  const [listPage, setListPage] = useState(1)
  const [listPageSize, setListPageSize] = useState(10)
  const [dataLoading, setDataLoading] = useState(false)
  const [summaryStats, setSummaryStats] = useState<{
    totalValue: number
    availableValue: number
    totalCount: number
    availableCount: number
  } | null>(null)

  const fetchSummary = useCallback((overrideType?: string) => {
    const typeVal = overrideType ?? appliedAssetType
    const body = {
      filters: {
        type: typeVal === 'all' ? undefined : typeVal,
        status: undefined as string | undefined,
      },
    }
    dataSourcesApi.summary(body)
      .then((res: any) => {
        const d = res?.data
        if (d && (typeof d.totalValue === 'number' || typeof d.totalCount === 'number')) {
          setSummaryStats({
            totalValue: Number(d.totalValue ?? 0),
            availableValue: Number(d.availableValue ?? d.activeValue ?? 0),
            totalCount: Number(d.totalCount ?? 0),
            availableCount: Number(d.availableCount ?? d.activeCount ?? 0),
          })
        } else {
          setSummaryStats(null)
        }
      })
      .catch(() => setSummaryStats(null))
  }, [appliedAssetType])

  const fetchData = (
    overridePage?: number,
    overridePageSize?: number,
    overrideFilters?: FilterOverrides
  ) => {
    setDataLoading(true)
    const typeVal = overrideFilters?.type ?? appliedAssetType
    const statusVal = overrideFilters?.status
    const dateRangeToUse = overrideFilters?.dateRange !== undefined ? overrideFilters.dateRange : appliedDateRange

    // 用 'keyword' in overrideFilters 判断是否显式传了关键词（含清空），避免传 undefined 时仍用旧的 appliedKeyword
    const keywordToUse =
      overrideFilters && 'keyword' in overrideFilters
        ? (overrideFilters.keyword ?? '')
        : appliedKeyword
    const filters: Record<string, string> = {}
    if (typeVal !== 'all') filters.type = typeVal
    if (statusVal && statusVal !== 'all') filters.status = statusVal
    if (keywordToUse && String(keywordToUse).trim()) filters.keyword = String(keywordToUse).trim()
    if (dateRangeToUse?.[0] && dateRangeToUse?.[1]) {
      filters.dateFrom = dateRangeToUse[0].format('YYYY-MM-DD')
      filters.dateTo = dateRangeToUse[1].format('YYYY-MM-DD')
    }

    const p = overridePage ?? listPage
    const ps = overridePageSize ?? listPageSize
    const body: Record<string, unknown> = {
      page: p,
      pageSize: ps,
    }
    if (Object.keys(filters).length) {
      body.filters = filters
    }
    dataSourcesApi.list(body)
      .then((res: any) => {
        const rows = normalizeDataList(res)
        const data = res?.data
        setTableData(rows.length ? rows : [])
        setListTotal(Number(data?.total ?? data?.totalCount ?? rows.length))
        setListPage(Number(data?.page ?? p))
        setListPageSize(Number(data?.pageSize ?? ps))
      })
      .catch(() => { /* 失败保持当前/mock 数据 */ })
      .finally(() => setDataLoading(false))
  }

  useEffect(() => { fetchData() }, [])
  useEffect(() => { fetchSummary() }, [fetchSummary])

  // 从头部搜索跳转过来时应用关键词、刷新列表，若有 highlightId 则打开该条详情
  useEffect(() => {
    const state = location.state as { searchKeyword?: string; highlightId?: string } | null
    const kw = state?.searchKeyword?.trim()
    const highlightId = state?.highlightId
    if (kw) {
      setAppliedKeyword(kw)
      setKeywordInput(kw)
      setListPage(1)
      fetchData(1, listPageSize, { keyword: kw })
    }
    if (highlightId) {
      handleView({ id: highlightId })
    }
    if (kw || highlightId) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  // 列表由后端按 filters(type, status, dateFrom, dateTo) 筛选返回，直接展示
  const displayData = tableData

  const handleRefresh = () => {
    setAssetType('all')
    setDateRange(null)
    setAppliedAssetType('all')
    setAppliedDateRange(null)
    setAppliedKeyword('')
    setKeywordInput('')
    setListPage(1)
    fetchData(1, listPageSize, { type: 'all', dateRange: null })
    fetchSummary('all')
    message.success('数据已刷新')
  }

  const handleFilter = () => {
    const kw = keywordInput.trim()
    setAppliedAssetType(assetType)
    setAppliedDateRange(dateRange)
    setAppliedKeyword(kw)
    setListPage(1)
    fetchData(1, listPageSize, { type: assetType, dateRange, keyword: kw || undefined })
    fetchSummary(assetType)
    message.success('已应用筛选条件')
  }

  const handleDelete = (key: string) => {
    const record = tableData.find(item => item.key === key)
    const id = record?.id ?? key
    dataSourcesApi.delete({ id })
      .then(() => {
        setTableData(prev => prev.filter(item => item.key !== key))
        message.success('数据项已删除')
      })
      .catch(() => {
        message.error('删除失败，请重试')
      })
  }

  const handleView = (record: any) => {
    const renderContent = (r: any) => (
      <div className="space-y-3 mt-2">
        <Row gutter={16}>
          <Col span={12}>
            <div className="text-xs text-gray-500">数据项ID</div>
            <div className="font-mono">{r.id}</div>
          </Col>
          <Col span={12}>
            <div className="text-xs text-gray-500">类型</div>
            <Tag color="blue">{r.type}</Tag>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <div className="text-xs text-gray-500">来源</div>
            <div>{r.source}</div>
          </Col>
          <Col span={12}>
            <div className="text-xs text-gray-500">资产可用状态</div>
            <Tag color={statusColorMap[r.status]}>
              {statusTextMap[r.status]}
            </Tag>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <div className="text-xs text-gray-500">价值 (¥)</div>
            <div className="font-bold">
              ¥{Number(r.value ?? 0).toLocaleString()}
            </div>
          </Col>
          <Col span={12}>
            <div className="text-xs text-gray-500">更新时间</div>
            <div>{r.updateTime ?? r.updated_at ?? r.update_time}</div>
          </Col>
        </Row>
        <div>
          <div className="text-xs text-gray-500">数据摘要</div>
          <div className="text-sm text-gray-700">{r.summary ?? r.description}</div>
        </div>
      </div>
    )
    dataSourcesApi.detail({ id: record.id })
      .then((res: any) => {
        const d = res?.data ?? res
        const detailRow = d && typeof d === 'object' ? {
          id: d.id ?? record.id,
          name: d.name ?? d.data_name ?? record.name,
          type: d.type ?? record.type,
          source: d.source ?? record.source,
          status: d.status ?? record.status,
          value: d.value ?? d.amount ?? record.value,
          updateTime: d.updateTime ?? d.updated_at ?? d.update_time ?? record.updateTime,
          summary: d.summary ?? d.description ?? record.summary,
        } : record
        Modal.info({
          title: detailRow.name,
          width: 640,
          content: renderContent(detailRow),
        })
      })
      .catch(() => {
        Modal.info({
          title: record.name,
          width: 640,
          content: renderContent(record),
        })
      })
  }

  const columns = [
    {
      title: '数据项ID',
      dataIndex: 'id',
      key: 'id',
      width: 140,
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: '数据名称',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (text: string) => <span className="font-medium">{text}</span>,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 160,
    },
    {
      title: '数据摘要',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
    },
    {
      title: '价值 (¥)',
      dataIndex: 'value',
      key: 'value',
      width: 160,
      render: (value: number) => (
        <span className="font-bold">¥{value.toLocaleString()}</span>
      ),
    },
    {
      title: '资产可用状态',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => (
        <Tag color={statusColorMap[status]}>
          {statusTextMap[status]}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleView(record)}>
            查看
          </Button>
          <Permission code="data_sources:delete">
            <Popconfirm
              title="确认删除该数据项？"
              description="删除后将无法恢复，请确认已完成备份。"
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record.key)}
            >
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          </Permission>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">资产数据</h1>
          <p className="text-gray-600">统一管理与查看数字资产数据明细</p>
        </div>
        <Space>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={dataLoading}>刷新数据</Button>
        </Space>
      </div>

      {/* 数据概览：优先使用 /api/data-sources/summary，失败时用当前数据兜底 */}
      {(() => {
        const stats = summaryStats ?? {
          totalValue: tableData.reduce((sum, item) => sum + item.value, 0),
          availableValue: tableData
            .filter(item => item.status === 'available')
            .reduce((sum, item) => sum + item.value, 0),
          totalCount: tableData.length,
          availableCount: tableData.filter(item => item.status === 'available').length,
        }
        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="总资产价值"
                  value={stats.totalValue}
                  prefix="¥"
                  valueStyle={{ color: '#3b82f6' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="可用资产价值"
                  value={stats.availableValue}
                  prefix="¥"
                  valueStyle={{ color: '#10b981' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="资产数量总量"
                  value={stats.totalCount}
                  valueStyle={{ color: '#8b5cf6' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="可用资产数据总量"
                  value={stats.availableCount}
                  valueStyle={{ color: '#f59e0b' }}
                />
              </Card>
            </Col>
          </Row>
        )
      })()}

      {/* 筛选条件 */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div>
            <div className="text-sm text-gray-500 mb-2">关键词</div>
            <Input
              placeholder="搜索数据名称、ID"
              value={keywordInput}
              onChange={e => setKeywordInput(e.target.value)}
              allowClear
              style={{ width: 200 }}
            />
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-2">时间范围</div>
            <RangePicker value={dateRange} onChange={dates => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)} />
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-2">资产类型</div>
            <Select
              value={assetType}
              onChange={setAssetType}
              style={{ width: 200 }}
            >
              <Option value="all">全部资产</Option>
              <Option value="应收">应收</Option>
              <Option value="票据">票据</Option>
              <Option value="保理资产">保理资产</Option>
              <Option value="ABS底层资产">ABS底层资产</Option>
            </Select>
          </div>
          <div className="flex items-end">
            <Button icon={<FilterOutlined />} type="primary" onClick={handleFilter}>
              筛选
            </Button>
          </div>
        </div>
      </Card>

      {/* 数据展示标签页 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'table',
              label: (
                <span>
                  <TableOutlined />
                  表格视图
                </span>
              ),
              children: (
                <Table
                  dataSource={displayData}
                  columns={columns}
                  loading={dataLoading}
                  pagination={{
                    current: listPage,
                    pageSize: listPageSize,
                    total: listTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (t) => `共 ${t} 条`,
                    onChange: (page, pageSize) => {
                      setListPage(page)
                      setListPageSize(pageSize || listPageSize)
                      fetchData(page, pageSize || listPageSize)
                    },
                  }}
                  rowClassName="hover:bg-gray-50 cursor-pointer"
                  scroll={{ x: 1000 }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* 数据详情 */}
      {/* <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="资产类型分布">
            <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <PieChartOutlined className="text-4xl text-gray-400 mb-4" />
                <div className="text-gray-500">资产分布图</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="增长趋势">
            <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <LineChartOutlined className="text-4xl text-gray-400 mb-4" />
                <div className="text-gray-500">增长趋势图</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row> */}
    </div>
  )
}

export default DataVisualization